import { FC, createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import Tooltip from '@mui/material/Tooltip'

import useHover from 'hooks/useHover'

import { ChangeVisabilityButton, DropDownOptionsContainer, DropDownTitle, DrowDownOptions, useDropDownStyles } from './DropDown-styles'
import { IChangeVisibilityButton, IContext, IDropDown, IDropDownContentWithTitle, ISmallerDropDown } from './DropDown.interfaces'

const Context = createContext({})

export const DropDown: FC<IDropDown> = ({
    children,
    header,
    type,
    open,
    larger = false,
    callback = null,
    disableLarger = null,
    className = '',
}) => {
    const [visibility, setVisibility] = useState<boolean>(open)

    useEffect(() => {
        setVisibility(open)
    }, [open])

    const handleVisibility = () => {
        !!callback && callback(!visibility)
        setVisibility(!visibility)
    }

    return (
        <>
            {!larger && !disableLarger && (
                <SmallerDropDown
                    header={header}
                    type={type}
                    larger={larger}
                    setVisibility={handleVisibility}
                    visibility={visibility}
                    childProps={children}
                    className={className}
                />
            )}
            {larger && !disableLarger && (
                <LargerWithBorderCollapse
                    header={header}
                    type={type}
                    larger={larger}
                    setVisibility={handleVisibility}
                    visibility={visibility}
                    childProps={children}
                    className={className}
                />
            )}
            {visibility && children[1]}
        </>
    )
}

const SmallerDropDown: FC<ISmallerDropDown> = ({ header, type, larger, setVisibility, visibility, childProps, className }) => {
    const classes = useDropDownStyles()

    const [hoveredState, changeHoveredState] = useHover()
    const useHoverState = type === 'eye' ? hoveredState : true

    return (
        <>
            <div className={clsx(classes.splitter, className)} {...changeHoveredState}>
                <DropDownTitle larger={larger} className="drop-down-title">
                    {header}
                </DropDownTitle>
                <div className={classes.splitterLine} />
                {useHoverState && (
                    <Context.Provider value={{ setVisibility, visibility, type }}>
                        <DrowDownOptions type={type}>{childProps && childProps[0]}</DrowDownOptions>
                    </Context.Provider>
                )}
            </div>
        </>
    )
}

const LargerWithBorderCollapse: FC<ISmallerDropDown> = ({ header, type, larger, setVisibility, visibility, childProps, className }) => {
    const classes = useDropDownStyles()
    const { t } = useTranslation()

    const [hoveredState, changeHoveredState] = useHover()
    const useHoverState = type === 'eye' ? hoveredState : true

    return (
        <>
            <div
                className={clsx(
                    classes.splitter,
                    classes.splitterLarger,
                    className,
                    !visibility && classes.collapsedDropDown,
                    type === 'button' && classes.noMargin
                )}
                {...changeHoveredState}
            >
                <DropDownTitle larger={larger} className="drop-down-title">
                    {t(`${header}`)}
                </DropDownTitle>
                {larger && visibility && <div className={classes.splitterLine}></div>}
                {useHoverState && (
                    <Context.Provider value={{ setVisibility, visibility, type }}>
                        <DrowDownOptions type={type}>{childProps && childProps[0]}</DrowDownOptions>
                    </Context.Provider>
                )}
            </div>
        </>
    )
}

export const DropDownOptions = ({ children = undefined }: { children?: React.ReactNode }) => (
    <DropDownOptionsContainer data-sta-id="DropDown-DropDownOptionsContainer">{children}</DropDownOptionsContainer>
)

export const DropDownContent = ({ children }: { children: React.ReactNode }) => <>{children}</>

export const DropDownContentWithTitle: FC<IDropDownContentWithTitle> = ({ header, gutterBottom, larger, children }) => {
    const classes = useDropDownStyles()

    return (
        <>
            <div className={classes.splitter} style={{ marginBottom: !gutterBottom && 30 }}>
                <DropDownTitle larger={larger} className="drop-down-title">
                    {header}
                </DropDownTitle>
                <div className={classes.splitterLine}></div>
            </div>
            {children}
        </>
    )
}

export const ChangeVisibilityButton: FC<IChangeVisibilityButton> = ({ size, bgColor }) => {
    const { t } = useTranslation()
    const { setVisibility, visibility, type } = useContext<IContext>(Context)

    return (
        <Tooltip title={visibility ? t('DropDownComponent.Hide') : t('DropDownComponent.Show')} placement="top" arrow={true}>
            <ChangeVisabilityButton
                type={type}
                isVisible={visibility}
                size={size}
                bgColor={bgColor}
                onClick={() => setVisibility(prev => !prev)}
            />
        </Tooltip>
    )
}
